import Head from "next/head"
import useMemberStore from "@/store/MemberStore"
import Image from "next/image"
import Link from "next/link"
import HrefLang from "@/components/HrefLang"
import * as amplitude from "@amplitude/analytics-browser"
import { Button } from "@/components/ui/button"
import { useRouter } from "next/router"
import { useEffect } from "react"
import { useTranslations } from "next-intl"

export default function Home() {
  const router = useRouter()
  const { locale } = router
  const { member } = useMemberStore()
  const t = useTranslations("HomePage")
  const title = t("Meta.title")
  const description = t("Meta.description")

  useEffect(() => {
    const accounts = member ? member.connectedThreadsAccounts : []
    const account = accounts.length > 0 ? accounts[0] : null
    if (!member) {
      return
    }
    if (account) {
      const url = `/dashboard/${account.username}`
      router.push(url)
    } else {
      const url = `/threads/connect`
      router.push(url)
    }
  }, [member, router])

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Head>
        <title>{title}</title>
        <meta name="og:title" content={title} />
        <meta name="og:description" content={description} />
        <meta name="description" content={description} />
      </Head>
      <HrefLang />

      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {t("Title.mainHeading")}
            <span className="text-red-600 block">{t("Title.subHeading")}</span>
          </h1>

          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            {t("Description.mainDescriptionPart1")}
            <br />
            {t("Description.mainDescriptionPart2")}
            <span className="font-medium text-gray-800">
              {t("Description.mainDescriptionHighlighted")}
            </span>
            <br />
            {t("Description.mainDescriptionPart3")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
            <Button
              size="lg"
              className="bg-red-500 hover:bg-red-600 text-lg px-8 py-6"
              onClick={() => {
                amplitude.logEvent("LandingPage_FreeUse_Clicked", {
                  position: "hero",
                })
                router.push("/signup")
              }}>
              {t("Action.freeUse")}
            </Button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600 mb-2">5000+</div>
              <div className="text-gray-600">{t("Message.activeUsers")}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-red-600 mb-2">10000+</div>
              <div className="text-gray-600">{t("Message.scheduledPosts")}</div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t("Title.dataAnalysis")}
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t("Description.dataAnalysisDescription")}
              </p>
              <Button
                size="lg"
                className="bg-red-500 hover:bg-red-600 text-lg"
                onClick={() => {
                  amplitude.logEvent("LandingPage_FreeUse_Clicked", {
                    position: "dataAnalysis",
                  })
                }}>
                {t("Action.freeUse")}
              </Button>
            </div>
            <div className="relative">
              <div className="bg-gradient-to-br from-blue-100 to-purple-100 rounded-2xl p-8 shadow-2xl">
                <Image
                  src={`/landingpage-${locale}-1.jpg`}
                  alt={t("Message.dataAnalysisDashboard")}
                  width={600}
                  height={400}
                  className="rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            <div className="lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t("Title.schedulePostHeading")}
                <span className="text-green-600">
                  {t("Title.schedulePostSubHeading")}
                </span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t("Description.schedulePostDescription")}
              </p>
              <Button
                size="lg"
                className="bg-red-500 hover:bg-red-600 text-lg"
                onClick={() => {
                  router.push("/signup")
                  amplitude.logEvent("LandingPage_FreeUse_Clicked", {
                    position: "schedulePost",
                  })
                }}>
                {t("Action.freeUse")}
              </Button>
            </div>
            <div className="lg:order-1 relative">
              <div className="bg-gradient-to-br from-green-100 to-blue-100 rounded-2xl p-8 shadow-2xl">
                <Image
                  src={`/landingpage-${locale}-5.jpg`}
                  alt={t("Message.schedulePostInterface")}
                  width={600}
                  height={400}
                  className="rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {t("Title.multiAccountManagement")}
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {t("Description.multiAccountManagementDescription")}
              </p>
              <Button
                size="lg"
                className="bg-red-500 hover:bg-red-600 text-lg"
                onClick={() => {
                  amplitude.logEvent("LandingPage_FreeUse_Clicked", {
                    position: "multiAccount",
                  })
                  router.push("/signup")
                }}>
                {t("Action.freeUse")}
              </Button>
            </div>
            <div className="relative">
              <div className="bg-[#F9FBFD] rounded-2xl shadow-2xl">
                <Image
                  src="/dashboard-img-multi-account.png"
                  alt={t("Message.multiAccountDashboard")}
                  width={600}
                  height={400}
                  className="rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="lg:order-2">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                多帳號發文
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                一鍵同步多個帳號發佈內容，提升品牌矩陣營運效率。
              </p>
              <Button size="lg" className="bg-red-500 hover:bg-red-600 text-lg">
                免費試用
              </Button>
            </div>
            <div className="lg:order-1 relative">
              <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-8 shadow-2xl">
                <Image
                  src="/placeholder.svg?height=400&width=600"
                  alt="多帳號發文介面"
                  width={600}
                  height={400}
                  className="rounded-lg shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-red-500 to-pink-600">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              {t("Title.whyChooseThreadslytics")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 text-left">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-white text-xl font-semibold mb-3">
                  {t("Title.efficientOperations")}
                </h3>
                <p className="text-red-100 leading-relaxed">
                  {t("Description.efficientOperationsDescription")}
                </p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6">
                <h3 className="text-white text-xl font-semibold mb-3">
                  {t("Title.dataDrivenStrategy")}
                </h3>
                <p className="text-red-100 leading-relaxed">
                  {t("Description.dataDrivenStrategyDescription")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold text-red-500 mb-4">
                {t("Title.threadslytics")}
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-4">{t("Title.about")}</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link
                    href="http://blog-tw.threadslytics.com/"
                    className="hover:text-white">
                    {t("Title.blog")}
                  </Link>
                </li>
                <li>
                  <Link
                    href="mailto:johnny@threadslytics.com"
                    className="hover:text-white">
                    {t("Title.contactUs")}
                  </Link>
                </li>
              </ul>
            </div>
            {/* <div>
              <h3 className="font-semibold mb-4">支援</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link href="#" className="hover:text-white">
                    幫助中心
                  </Link>
                </li>
                <li>
                  <Link href="#" className="hover:text-white">
                    聯絡我們
                  </Link>
                </li>
                <li>
                  <Link href="#" className="hover:text-white">
                    API 文件
                  </Link>
                </li>
              </ul>
            </div> */}
            {/* <div>
              <h3 className="font-semibold mb-4">公司</h3>
              <ul className="space-y-2 text-gray-400">
                <li>
                  <Link href="#" className="hover:text-white">
                    關於我們
                  </Link>
                </li>
                <li>
                  <Link href="#" className="hover:text-white">
                    隱私政策
                  </Link>
                </li>
                <li>
                  <Link href="#" className="hover:text-white">
                    服務條款
                  </Link>
                </li>
              </ul>
            </div> */}
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>
              &copy; 2025 {t("Title.threadslytics")}.{" "}
              {t("Message.allRightsReserved")}
            </p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export async function getServerSideProps(context) {
  return {
    props: {
      messages: (await import(`@/messages/${context.locale}.json`)).default,
    },
  }
}
